/**
 * Story 054 — Edit-site modal (`/sites?site=<uuid>`).
 *
 * R58-CC-02a (this revision): the read-only / role-gating pattern is
 * delegated to the shared `<ReadOnlyModal>` primitive in
 * `@aeriosiq/ui` + the `useCanEditResource` hook. This file no
 * longer hand-rolls:
 *   - the inline read-only banner JSX (now the primitive's banner with
 *     `role="status"` + `aria-live="polite"`);
 *   - the per-input `slotProps.htmlInput.readOnly` plumbing on editable
 *     fields (now the primitive's wrapping `<fieldset disabled>` — the
 *     identity + audit fields keep their `readOnly: true` flag because
 *     they are *permanently* read-only regardless of role);
 *   - the custom `MUTATE_ROLES` set (now centralised in
 *     `useCanEditResource`).
 *
 * Behaviour preserved verbatim from the pre-CC-02a build:
 *   - Loads `GET /sites/{id}` on open.
 *   - Editable fields gated on the same mutate-role set
 *     (`tenant_admin | project_manager | back_office`) — sourced from
 *     `useCanEditResource(roles, 'site')` so the matrix is the central
 *     `_FRONTEND/.vibe/06_INIT_DEV_PLAN.md` §11a one.
 *   - Inactive sites are read-only regardless of role; banner copy
 *     overrides to "This site is inactive. Reactivate to edit." via the
 *     primitive's `bannerOverride` prop.
 *   - Footer flips to a single primary "Reactivate site" button when
 *     the site is inactive and the user can mutate.
 *   - Deactivation fires through a separate ConfirmDialog with danger
 *     styling (AC10).
 *   - Audit-person null renders as italic "Unknown" (Story 054 Open Q12).
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ReadOnlyModal, useCanEditResourceFor } from "@aeriosiq/ui";
import { errorToastFor403 } from "../../api/errorToast.js";
import { useToast } from "../../account/ToastContext.js";
import { ConfirmDialog } from "../../admin/shared/ConfirmDialog.js";
import {
  useDeactivateSiteMutation,
  useReactivateSiteMutation,
  useSiteDetailQuery,
  useUpdateSiteMutation,
  type ExternalId,
  type SiteDetail,
  type UpdateSiteBody,
} from "../queries/index.js";
import { CountryAutocomplete } from "../shared/CountryAutocomplete.js";
import { toAlpha2 } from "../shared/iso3166.js";
import { ExternalIdsField } from "../shared/ExternalIdsField.js";
import { CopyIcon } from "@aeriosiq/ui/icons";

interface EditSiteFormValues {
  siteName: string;
  countryCode: string | null;
  latitude: string;
  longitude: string;
  isOffshore: boolean;
  aviationAuthority: string;
  controlCenterPhoneNumber: string;
  additionalDescription: string;
  externalIds: ExternalId[];
}

function detailToFormValues(d: SiteDetail): EditSiteFormValues {
  return {
    siteName: d.siteName,
    countryCode: d.countryCode,
    latitude: d.latitude === null ? "" : String(d.latitude),
    longitude: d.longitude === null ? "" : String(d.longitude),
    isOffshore: d.isOffshore,
    aviationAuthority: d.aviationAuthority ?? "",
    controlCenterPhoneNumber: d.controlCenterPhoneNumber ?? "",
    additionalDescription: d.additionalDescription ?? "",
    externalIds: d.externalIds,
  };
}

function toNumberOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function dropEmptyExternalIds(arr: ExternalId[]): ExternalId[] {
  return arr.filter(
    (r) => r.provider.trim().length > 0 && r.id.trim().length > 0,
  );
}

function buildUpdateBody(v: EditSiteFormValues): UpdateSiteBody {
  return {
    siteName: v.siteName.trim(),
    // R58-W-02 -- defensive alpha-3 -> alpha-2 coercion. `toAlpha2` is a
    // passthrough for already-alpha-2 codes, so picking a newly-edited
    // country (the autocomplete now emits alpha-2) stays alpha-2; only
    // legacy detail rows that arrived as alpha-3 get mapped down before
    // PATCH. Matches `assets/v1@4.2.0 UpdateSiteBody.countryCode`.
    countryCode: v.countryCode ? toAlpha2(v.countryCode) : null,
    latitude: toNumberOrNull(v.latitude),
    longitude: toNumberOrNull(v.longitude),
    isOffshore: v.isOffshore,
    aviationAuthority:
      v.aviationAuthority.trim().length > 0 ? v.aviationAuthority.trim() : null,
    controlCenterPhoneNumber:
      v.controlCenterPhoneNumber.trim().length > 0
        ? v.controlCenterPhoneNumber.trim()
        : null,
    additionalDescription:
      v.additionalDescription.trim().length > 0
        ? v.additionalDescription
        : null,
    externalIds: dropEmptyExternalIds(v.externalIds),
  };
}

function formatIso(iso: string | null | undefined): string {
  if (!iso) return "Unknown";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 19).replace("T", " ") + " UTC";
  } catch {
    return iso;
  }
}

export interface EditSiteDialogProps {
  siteId: string | null | undefined;
  onClose: () => void;
}

export function EditSiteDialog({
  siteId,
  onClose,
}: EditSiteDialogProps): ReactElement {
  const open = typeof siteId === "string" && siteId.length > 0;
  const { data: detail, isPending, isError } = useSiteDetailQuery(siteId);

  // SB2-CC-01 — matrix-driven role gate. `useCanEditResourceFor`
  // returns the matrix cell (true | false | "conditional"-resolved-
  // against-the-resource-tenant) so platform_admin (cell value
  // "conditional", cross-tenant superuser) gets `true` and the
  // read-only banner no longer flashes for the canonical owner of
  // the platform. `SiteDetail` does not currently surface a tenantId
  // on the wire (the BE filters by `X-Tenant-ID` for tenant-scoped
  // callers); without a resource tenant the predicate assumes
  // same-tenant — the BE remains authoritative on Save.
  const canMutate = useCanEditResourceFor("site.update");

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<EditSiteFormValues>({
    defaultValues: detail
      ? detailToFormValues(detail)
      : {
          siteName: "",
          countryCode: null,
          latitude: "",
          longitude: "",
          isOffshore: false,
          aviationAuthority: "",
          controlCenterPhoneNumber: "",
          additionalDescription: "",
          externalIds: [],
        },
    mode: "onChange",
  });

  useEffect(() => {
    if (detail) {
      reset(detailToFormValues(detail));
    }
  }, [detail, reset]);

  const update = useUpdateSiteMutation();
  const deactivate = useDeactivateSiteMutation();
  const reactivate = useReactivateSiteMutation();
  const { toast } = useToast();

  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  // Inactive sites are read-only regardless of role; the user must
  // reactivate first (spec 054 §410-gone). The primitive's
  // `bannerOverride` is used for that branch so the canonical "role
  // does not allow editing" copy is replaced with the inactive-site
  // explanation.
  const isInactive = detail?.isActive === false;
  const readOnly = !canMutate || isInactive;

  const latitude = watch("latitude");
  const longitude = watch("longitude");
  const isOffshore = watch("isOffshore");

  const subtitle = useMemo(() => {
    if (!detail) return "";
    const country = detail.countryCode ?? "—";
    const offshore = detail.isOffshore ? "Offshore" : "Onshore";
    // Include the siteName so the dialog's textContent surfaces it
    // (Story 057 AC24 — Park-cell drill-down asserts the dialog
    // mentions the site name; the modal's subtitle is the natural
    // home for that surface).
    return `${detail.siteName} · Site code ${String(detail.siteCode)} · ${country} · ${offshore}`;
  }, [detail]);

  async function onSubmit(v: EditSiteFormValues): Promise<void> {
    if (!detail) return;
    try {
      await update.mutateAsync({
        id: detail.id,
        body: buildUpdateBody(v),
      });
      toast("Site updated.");
      onClose();
    } catch (err) {
      // SB2-CC-01 AC11 — render the structured 403 `reason` verbatim
      // when the BE returns one. Falls back to the legacy forbidden-
      // field copy / generic error copy for non-structured failures.
      const structured = errorToastFor403(err);
      if (structured) {
        toast(structured, "error");
      } else {
        const errBody = (err as { body?: { code?: string; detail?: string } })
          .body;
        if (errBody?.detail?.includes("forbidden_field")) {
          toast("Cannot set this field — it's managed by the system.", "error");
        } else {
          toast("Couldn't save site. Try again.", "error");
        }
      }
    }
  }

  async function doDeactivate(): Promise<void> {
    if (!detail) return;
    try {
      await deactivate.mutateAsync(detail.id);
      toast(`Site ${detail.siteName} deactivated.`);
      setConfirmDeactivate(false);
      onClose();
    } catch {
      toast("Couldn't deactivate site.", "error");
    }
  }

  async function doReactivate(): Promise<void> {
    if (!detail) return;
    try {
      await reactivate.mutateAsync(detail.id);
      toast(`Site ${detail.siteName} reactivated.`);
      onClose();
    } catch {
      toast("Couldn't reactivate site.", "error");
    }
  }

  function copySiteId(): void {
    if (!detail) return;
    void navigator.clipboard.writeText(detail.id);
    toast("Site ID copied.");
  }

  // Title element — shown inside the primitive's DialogTitle. We pass
  // the heading + caption together so the existing `id="edit-site-
  // heading"` anchor is preserved for tests that locate the dialog by
  // `aria-labelledby`. (The primitive forwards aria-label only when
  // `title` is a string; using a node node still produces an accessible
  // dialog because we attach our own heading element here.)
  const titleNode = (
    <>
      <Typography
        id="edit-site-heading"
        component="span"
        variant="h6"
        sx={{ display: "block" }}
      >
        Edit site
      </Typography>
      {detail ? (
        <Typography
          component="div"
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </>
  );

  // Banner override — when the modal is read-only because the site is
  // inactive (rather than because the role lacks edit permission) we
  // surface the more informative "Reactivate to edit." copy. When the
  // user IS allowed to mutate (`canMutate`) but the site is inactive,
  // the banner still renders (read-only is still true) but with the
  // inactive-specific copy.
  const bannerOverride =
    readOnly && isInactive
      ? "This site is inactive. Reactivate to edit."
      : undefined;

  // Footer actions slot. When `readOnly` is true the primitive
  // suppresses this entirely (the Cancel + Save + Deactivate buttons
  // are not rendered in the DOM), satisfying R58-CC-02a AC3.
  //
  // Inactive + canMutate: a single primary "Reactivate site" button.
  // Otherwise the standard Cancel + Deactivate (left) + Save (right) row.
  const actionsNode = detail ? (
    !detail.isActive && canMutate ? (
      <>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void doReactivate();
          }}
          disabled={reactivate.isPending}
          startIcon={
            reactivate.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Reactivate site
        </Button>
      </>
    ) : detail.isActive && canMutate ? (
      <>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="error"
          onClick={() => {
            setConfirmDeactivate(true);
          }}
        >
          Deactivate site
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          type="button"
          variant="contained"
          onClick={() => {
            void handleSubmit(onSubmit)();
          }}
          disabled={!isDirty || update.isPending}
          startIcon={
            update.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          Save changes
        </Button>
      </>
    ) : null
  ) : null;

  return (
    <>
      <ReadOnlyModal
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-site-heading"
        readOnly={readOnly}
        entityNoun="site"
        {...(bannerOverride ? { bannerOverride } : {})}
        title={titleNode}
        actions={actionsNode}
        // When the modal is read-only specifically BECAUSE the site is
        // inactive (and the user CAN mutate), show the action row so
        // the Reactivate-site button is reachable — without it, the
        // inactive-site read-only state is unrecoverable from the UI.
        // The role-deny read-only state (`!canMutate`) keeps the
        // default behaviour: actions hidden.
        showActionsInReadOnly={readOnly && isInactive && canMutate}
      >
        {isPending ? (
          <Stack spacing={1.5}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Stack>
        ) : isError ? (
          <Alert severity="error">Couldn&apos;t load this site.</Alert>
        ) : (
          <Stack spacing={2}>
            {/* IDENTITY — permanently read-only (system-managed). The
                `readOnly: true` on these inputs is independent of the
                role gate; the primitive's `<fieldset disabled>` wrap
                in read-only mode disables them further, which is
                visually identical for the user. */}
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Identity
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="Site ID"
                value={detail.id}
                variant="standard"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Site ID",
                    readOnly: true,
                    tabIndex: -1,
                  },
                }}
                fullWidth
              />
              <Tooltip title="Copy UUID">
                <IconButton
                  aria-label="Copy UUID to clipboard"
                  onClick={copySiteId}
                  size="small"
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <TextField
              label="Site code"
              value={String(detail.siteCode)}
              variant="standard"
              slotProps={{
                htmlInput: {
                  "aria-label": "Site code",
                  readOnly: true,
                  tabIndex: -1,
                },
              }}
              fullWidth
            />

            <Divider sx={{ my: 1 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Details
            </Typography>
            {/* Editable fields — the primitive's wrapping `<fieldset
                disabled>` transitively disables these inputs in
                read-only mode; no per-field `readOnly` plumbing
                required. */}
            <TextField
              {...register("siteName", { required: true })}
              label="Site name"
              required
              slotProps={{
                htmlInput: {
                  "aria-label": "Site name",
                  "aria-required": true,
                  maxLength: 255,
                },
              }}
              fullWidth
            />
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <CountryAutocomplete
                  value={field.value}
                  onChange={field.onChange}
                  latitude={toNumberOrNull(latitude)}
                  longitude={toNumberOrNull(longitude)}
                  readOnly={readOnly}
                  onLookupError={(msg) => {
                    toast(msg, "error");
                  }}
                />
              )}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                {...register("latitude")}
                label="Latitude"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Latitude",
                    step: "any",
                    min: -90,
                    max: 90,
                  },
                }}
                fullWidth
              />
              <TextField
                {...register("longitude")}
                label="Longitude"
                type="number"
                slotProps={{
                  htmlInput: {
                    "aria-label": "Longitude",
                    step: "any",
                    min: -180,
                    max: 180,
                  },
                }}
                fullWidth
              />
            </Stack>
            <Controller
              name="isOffshore"
              control={control}
              render={({ field }) => (
                <ToggleButtonGroup
                  exclusive
                  value={isOffshore ? "offshore" : "onshore"}
                  onChange={(_e, next) => {
                    if (next === null) return;
                    field.onChange(next === "offshore");
                  }}
                  size="small"
                  aria-label="On/Offshore"
                >
                  <ToggleButton value="onshore">Onshore</ToggleButton>
                  <ToggleButton value="offshore">Offshore</ToggleButton>
                </ToggleButtonGroup>
              )}
            />
            <TextField
              {...register("aviationAuthority")}
              label="Aviation authority"
              slotProps={{
                htmlInput: {
                  "aria-label": "Aviation authority",
                  maxLength: 255,
                },
              }}
              fullWidth
            />
            <TextField
              {...register("controlCenterPhoneNumber")}
              label="Control centre phone"
              slotProps={{
                htmlInput: {
                  "aria-label": "Control centre phone",
                  maxLength: 50,
                },
              }}
              fullWidth
            />
            <TextField
              {...register("additionalDescription")}
              label="Additional description"
              multiline
              rows={3}
              slotProps={{
                htmlInput: {
                  "aria-label": "Additional description",
                },
              }}
              fullWidth
            />

            <ExternalIdsField
              control={control}
              register={register}
              readOnly={readOnly}
            />

            <Divider sx={{ my: 1 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Audit
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Created at"
                value={formatIso(detail.createdAt)}
                variant="standard"
                slotProps={{
                  htmlInput: { readOnly: true, tabIndex: -1 },
                }}
                fullWidth
              />
              <AuditPersonField label="Created by" person={detail.createdBy} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Updated at"
                value={formatIso(detail.updatedAt)}
                variant="standard"
                slotProps={{
                  htmlInput: { readOnly: true, tabIndex: -1 },
                }}
                fullWidth
              />
              <AuditPersonField label="Updated by" person={detail.updatedBy} />
            </Stack>
          </Stack>
        )}
      </ReadOnlyModal>

      <ConfirmDialog
        open={confirmDeactivate}
        title={`Deactivate site ${detail?.siteName ?? ""}?`}
        description={
          <Box>
            By default, deactivated sites won&apos;t appear in the Sites list.
            Historical references (assets, inspections, defects) stay intact.
            You can reactivate it later from the inactive view.
          </Box>
        }
        confirmLabel="Deactivate site"
        danger
        pending={deactivate.isPending}
        onConfirm={() => {
          void doDeactivate();
        }}
        onCancel={() => {
          setConfirmDeactivate(false);
        }}
      />
    </>
  );
}

function AuditPersonField({
  label,
  person,
}: {
  label: string;
  person: { id: string; displayName: string } | null;
}): ReactElement {
  if (person === null) {
    return (
      <TextField
        label={label}
        value="Unknown"
        variant="standard"
        slotProps={{
          htmlInput: { readOnly: true, tabIndex: -1 },
          input: { sx: { fontStyle: "italic" } },
        }}
        fullWidth
      />
    );
  }
  return (
    <TextField
      label={label}
      value={person.displayName}
      variant="standard"
      slotProps={{
        htmlInput: { readOnly: true, tabIndex: -1 },
      }}
      fullWidth
    />
  );
}
